import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Rocket, CheckCircle, ArrowRight, ArrowLeft, User, Building2, Briefcase, 
  Palette, Code, Video, Megaphone, PenTool, Camera, Edit3, FileText,
  CreditCard, Upload, Check, X, Loader2, Sparkles, Users, HandCoins, Home
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import api from '../../lib/axios';

// Using api from lib/axios

const SPECIALTIES = [
  { id: 'graphic-design', label: 'Design Graphique', icon: Palette },
  { id: 'web-development', label: 'Développement Web', icon: Code },
  { id: 'video-production', label: 'Production Vidéo', icon: Video },
  { id: 'motion-design', label: 'Motion Design', icon: Sparkles },
  { id: 'photography', label: 'Photographie', icon: Camera },
  { id: 'copywriting', label: 'Rédaction', icon: Edit3 },
  { id: 'social-media', label: 'Social Media', icon: Megaphone },
  { id: 'illustration', label: 'Illustration', icon: PenTool },
];

const LEGAL_STATUS = [
  { value: 'auto-entrepreneur', label: 'Auto-entrepreneur' },
  { value: 'eurl', label: 'EURL' },
  { value: 'sarl', label: 'SARL' },
  { value: 'sas', label: 'SAS / SASU' },
  { value: 'other', label: 'Autre' },
];

const AVAILABILITY = [
  { value: 'full-time', label: 'Temps plein' },
  { value: 'part-time', label: 'Mi-temps' },
  { value: 'occasional', label: 'Missions ponctuelles' },
];

const CreativeOnboardingPage = () => {
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [onboardingId, setOnboardingId] = useState(null);
  const [contractHtml, setContractHtml] = useState('');
  const [completed, setCompleted] = useState(false);
  const [themeSettings, setThemeSettings] = useState(null);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const res = await api.get('/api/theme');
        setThemeSettings(res.data);
      } catch (e) {
        console.log('Theme not loaded');
      }
    };
    fetchTheme();
  }, []);
  
  const [formData, setFormData] = useState({
    // Type
    onboarding_type: 'creative',
    
    // Personal Info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    
    // Professional Info
    company_name: '',
    siret: '',
    legal_status: '',
    
    // Skills (for creatives)
    specialties: [],
    years_experience: 0,
    portfolio_url: '',
    linkedin_url: '',
    
    // For affiliates
    referral_source: '',
    has_network: false,
    estimated_referrals: 0,
    
    // Availability
    availability: '',
    preferred_projects: [],
    
    // Banking
    iban: '',
    bic: '',
    bank_name: '',
    
    // Additional
    bio: '',
    motivation: '',
    
    // Documents
    documents: {}
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSpecialty = (id) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(id)
        ? prev.specialties.filter(s => s !== id)
        : [...prev.specialties, id]
    }));
  };

  const steps = formData.onboarding_type === 'creative' 
    ? ['Type', 'Identité', 'Professionnel', 'Compétences', 'Bancaire', 'Motivation', 'Contrat']
    : ['Type', 'Identité', 'Professionnel', 'Réseau', 'Bancaire', 'Motivation', 'Contrat'];

  const canProceed = () => {
    switch (step) {
      case 0: return true;
      case 1: return formData.first_name && formData.last_name && formData.email && formData.phone;
      case 2: return formData.legal_status;
      case 3: 
        if (formData.onboarding_type === 'creative') {
          return formData.specialties.length > 0;
        }
        return true;
      case 4: return formData.iban && formData.bic;
      case 5: return formData.motivation.length > 20;
      case 6: return true;
      default: return true;
    }
  };

  const handleSubmitQuestionnaire = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/onboarding/`, {
        questionnaire: formData
      });
      setOnboardingId(response.data.id);
      
      // Fetch contract
      const contractRes = await api.get('/onboarding/${response.data.id}/contract');
      setContractHtml(contractRes.data.html);
      
      setStep(6);
      toast.success('Questionnaire enregistré !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez signer le contrat');
      return;
    }
    
    setLoading(true);
    try {
      const signatureData = signatureRef.current.toDataURL('image/png');
      
      await axios.post(`${API}/onboarding/${onboardingId}/sign`, {
        signature: signatureData
      });
      
      setCompleted(true);
      toast.success('Contrat signé avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const nextStep = () => {
    if (step === 5 && !onboardingId) {
      handleSubmitQuestionnaire();
    } else {
      setStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => setStep(prev => Math.max(prev - 1, 0));

  // Completed State
  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Candidature envoyée !</h1>
            <p className="text-slate-600 mb-6">
              Merci pour votre candidature. Notre équipe va étudier votre dossier et vous recontactera 
              dans les plus brefs délais avec vos identifiants de connexion.
            </p>
            <Button onClick={() => navigate('/site')} className="rounded-full">
              Retour au site
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 sm:px-0 overflow-x-hidden">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-lg border-b border-slate-700 sticky top-0 z-50 -mx-4 sm:mx-0 px-4 sm:px-0">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/site" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            {themeSettings?.logo?.lightUrl ? (
              <img 
                src={themeSettings.logo.lightUrl} 
                alt={themeSettings.agency?.name || "Logo"} 
                className="h-10 w-auto object-contain brightness-0 invert"
              />
            ) : (
              <>
                <div className="text-2xl font-black">
                  <span className="text-[#00d4aa]">M</span>
                  <span className="text-white">SC</span>
                </div>
                <span className="text-white/70 text-sm font-medium">L&apos;Agence</span>
              </>
            )}
          </Link>
          
          <Link 
            to="/site" 
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
          >
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">Retour au site</span>
          </Link>
          
          <Badge variant="outline" className="text-cyan-400 border-cyan-400">
            {formData.onboarding_type === 'creative' ? 'Devenir Créatif' : 'Devenir Apporteur'}
          </Badge>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-2">
          {steps.map((s, i) => (
            <div 
              key={i}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all",
                i < step ? "bg-emerald-500 text-white" :
                i === step ? "bg-cyan-500 text-white ring-4 ring-cyan-500/30" :
                "bg-slate-700 text-slate-400"
              )}
            >
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          {steps.map((s, i) => (
            <span key={i} className={cn("text-center", i === step && "text-cyan-400 font-medium")}>
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-2xl mx-auto px-4 pb-8">
        <Card className="bg-white/95 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-slate-800">
              {step === 0 && "Choisissez votre profil"}
              {step === 1 && "Informations personnelles"}
              {step === 2 && "Informations professionnelles"}
              {step === 3 && (formData.onboarding_type === 'creative' ? "Vos compétences" : "Votre réseau")}
              {step === 4 && "Coordonnées bancaires"}
              {step === 5 && "Motivation"}
              {step === 6 && "Signature du contrat"}
            </CardTitle>
            <CardDescription>
              {step === 0 && "Sélectionnez le type de collaboration que vous souhaitez"}
              {step === 1 && "Ces informations nous permettront de vous contacter"}
              {step === 2 && "Informations sur votre structure juridique"}
              {step === 3 && (formData.onboarding_type === 'creative' 
                ? "Sélectionnez vos domaines d'expertise" 
                : "Parlez-nous de votre réseau")}
              {step === 4 && "Pour le règlement de vos commissions"}
              {step === 5 && "Pourquoi souhaitez-vous nous rejoindre ?"}
              {step === 6 && "Lisez et signez le contrat de collaboration"}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Step 0: Type Selection */}
            {step === 0 && (
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={() => updateField('onboarding_type', 'creative')}
                  className={cn(
                    "p-6 rounded-xl border-2 text-left transition-all",
                    formData.onboarding_type === 'creative'
                      ? "border-cyan-500 bg-cyan-50"
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                    <Palette className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-1">Créatif Freelance</h3>
                  <p className="text-sm text-slate-600">
                    Designer, développeur, vidéaste... Rejoignez notre pool de talents créatifs.
                  </p>
                </button>
                
                <button
                  onClick={() => updateField('onboarding_type', 'affiliate')}
                  className={cn(
                    "p-6 rounded-xl border-2 text-left transition-all",
                    formData.onboarding_type === 'affiliate'
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center mb-4">
                    <HandCoins className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-1">Apporteur d'Affaires</h3>
                  <p className="text-sm text-slate-600">
                    Vous avez un réseau ? Gagnez des commissions en nous présentant des clients.
                  </p>
                </button>
              </div>
            )}

            {/* Step 1: Personal Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Prénom *</Label>
                    <Input 
                      value={formData.first_name}
                      onChange={(e) => updateField('first_name', e.target.value)}
                      placeholder="Jean"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Nom *</Label>
                    <Input 
                      value={formData.last_name}
                      onChange={(e) => updateField('last_name', e.target.value)}
                      placeholder="Dupont"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="jean@example.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Téléphone *</Label>
                    <Input 
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="06 12 34 56 78"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Adresse</Label>
                  <Input 
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="123 Rue de la Paix"
                    className="mt-1"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Code postal</Label>
                    <Input 
                      value={formData.postal_code}
                      onChange={(e) => updateField('postal_code', e.target.value)}
                      placeholder="13800"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input 
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="Istres"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Professional Info */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Statut juridique *</Label>
                  <Select 
                    value={formData.legal_status} 
                    onValueChange={(v) => updateField('legal_status', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Sélectionnez votre statut" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEGAL_STATUS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Nom de l'entreprise (si applicable)</Label>
                  <Input 
                    value={formData.company_name}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    placeholder="Ma Société"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>SIRET</Label>
                  <Input 
                    value={formData.siret}
                    onChange={(e) => updateField('siret', e.target.value)}
                    placeholder="123 456 789 00001"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Disponibilité</Label>
                  <Select 
                    value={formData.availability} 
                    onValueChange={(v) => updateField('availability', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Votre disponibilité" />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABILITY.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 3: Skills (Creative) or Network (Affiliate) */}
            {step === 3 && formData.onboarding_type === 'creative' && (
              <div className="space-y-4">
                <div>
                  <Label className="mb-3 block">Spécialités *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {SPECIALTIES.map((s) => {
                      const Icon = s.icon;
                      const selected = formData.specialties.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => toggleSpecialty(s.id)}
                          className={cn(
                            "p-3 rounded-xl border-2 text-center transition-all",
                            selected
                              ? "border-cyan-500 bg-cyan-50 text-cyan-700"
                              : "border-slate-200 hover:border-slate-300 text-slate-600"
                          )}
                        >
                          <Icon className="w-6 h-6 mx-auto mb-2" />
                          <span className="text-xs font-medium">{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <Label>Années d'expérience</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.years_experience}
                    onChange={(e) => updateField('years_experience', parseInt(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>URL Portfolio</Label>
                  <Input 
                    type="url"
                    value={formData.portfolio_url}
                    onChange={(e) => updateField('portfolio_url', e.target.value)}
                    placeholder="https://monportfolio.com"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Profil LinkedIn</Label>
                  <Input 
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => updateField('linkedin_url', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {step === 3 && formData.onboarding_type === 'affiliate' && (
              <div className="space-y-4">
                <div>
                  <Label>Comment avez-vous connu l'agence ?</Label>
                  <Input 
                    value={formData.referral_source}
                    onChange={(e) => updateField('referral_source', e.target.value)}
                    placeholder="Réseau professionnel, réseaux sociaux..."
                    className="mt-1"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <Checkbox 
                    checked={formData.has_network}
                    onCheckedChange={(checked) => updateField('has_network', checked)}
                  />
                  <Label>J'ai un réseau professionnel actif</Label>
                </div>
                
                <div>
                  <Label>Estimation du nombre de clients potentiels par an</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.estimated_referrals}
                    onChange={(e) => updateField('estimated_referrals', parseInt(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Profil LinkedIn</Label>
                  <Input 
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => updateField('linkedin_url', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Banking */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-amber-800">
                    <strong>Important :</strong> Ces informations sont nécessaires pour vous régler 
                    vos commissions. Elles sont stockées de manière sécurisée.
                  </p>
                </div>
                
                <div>
                  <Label>IBAN *</Label>
                  <Input 
                    value={formData.iban}
                    onChange={(e) => updateField('iban', e.target.value.toUpperCase())}
                    placeholder="FR76 1234 5678 9012 3456 7890 123"
                    className="mt-1 font-mono"
                  />
                </div>
                
                <div>
                  <Label>BIC/SWIFT *</Label>
                  <Input 
                    value={formData.bic}
                    onChange={(e) => updateField('bic', e.target.value.toUpperCase())}
                    placeholder="BNPAFRPP"
                    className="mt-1 font-mono"
                  />
                </div>
                
                <div>
                  <Label>Nom de la banque</Label>
                  <Input 
                    value={formData.bank_name}
                    onChange={(e) => updateField('bank_name', e.target.value)}
                    placeholder="BNP Paribas"
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Step 5: Motivation */}
            {step === 5 && (
              <div className="space-y-4">
                <div>
                  <Label>Courte bio</Label>
                  <Textarea 
                    value={formData.bio}
                    onChange={(e) => updateField('bio', e.target.value)}
                    placeholder="Présentez-vous en quelques lignes..."
                    className="mt-1 min-h-[100px]"
                  />
                </div>
                
                <div>
                  <Label>Pourquoi souhaitez-vous nous rejoindre ? *</Label>
                  <Textarea 
                    value={formData.motivation}
                    onChange={(e) => updateField('motivation', e.target.value)}
                    placeholder="Expliquez vos motivations et ce que vous pouvez apporter..."
                    className="mt-1 min-h-[150px]"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Minimum 20 caractères ({formData.motivation.length}/20)
                  </p>
                </div>
              </div>
            )}

            {/* Step 6: Contract */}
            {step === 6 && (
              <div className="space-y-4">
                {/* Contract Preview */}
                <div className="border rounded-xl overflow-hidden max-h-[400px] overflow-y-auto">
                  <div 
                    className="p-4 bg-white"
                    dangerouslySetInnerHTML={{ __html: contractHtml }}
                  />
                </div>
                
                {/* Signature Area */}
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-4">
                  <Label className="mb-2 block">Votre signature</Label>
                  <div className="bg-white border rounded-lg">
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                        width: 500,
                        height: 150,
                        className: 'w-full rounded-lg'
                      }}
                      backgroundColor="white"
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearSignature}
                    className="mt-2"
                  >
                    <X className="w-4 h-4 mr-1" /> Effacer
                  </Button>
                </div>
                
                <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl">
                  <Checkbox id="accept" />
                  <label htmlFor="accept" className="text-sm text-slate-600">
                    J'ai lu et j'accepte les termes du contrat de collaboration. 
                    Je certifie que les informations fournies sont exactes.
                  </label>
                </div>
              </div>
            )}
          </CardContent>

          {/* Navigation */}
          <div className="px-6 pb-6 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={step === 0}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            
            {step < 6 ? (
              <Button
                onClick={nextStep}
                disabled={!canProceed() || loading}
                className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-600"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {step === 5 ? 'Voir le contrat' : 'Continuer'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSign}
                disabled={loading}
                className="rounded-full bg-gradient-to-r from-emerald-500 to-green-600"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Signer et envoyer
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CreativeOnboardingPage;
